import { useEffect, useRef } from "react";

/**
 * Asks before leaving the page through an in-app link (sidebar, navbar,
 * "Kthehu te produktet") while there are unsaved changes.
 *
 * The app uses <BrowserRouter>, where react-router's navigation blockers are
 * not available, so this listens for link clicks in the capture phase —
 * before react-router's <Link> handler runs — and cancels only the click the
 * person declines. It is active only while `active` is true and this
 * component is mounted; the router itself is untouched.
 *
 * Not covered (by design): programmatic navigate() calls and the browser's
 * back/forward buttons. Tab/window close is covered by `beforeunload`.
 */
export const shouldGuardLinkClick = (event, currentLocation = window.location) => {
  if (event.defaultPrevented || event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  const anchor = event.target?.closest?.("a[href]");
  if (!anchor) return false;
  if ((anchor.target && anchor.target !== "_self") || anchor.hasAttribute("download")) return false;

  let url;
  try {
    url = new URL(anchor.getAttribute("href"), currentLocation.href);
  } catch (_error) {
    return false;
  }
  // Other sites are handled by beforeunload; same page (hash tabs) is fine.
  if (url.origin !== currentLocation.origin) return false;
  return url.pathname !== currentLocation.pathname || url.search !== currentLocation.search;
};

const useUnsavedLinkGuard = (active, message) => {
  const messageRef = useRef(message);
  messageRef.current = message;

  useEffect(() => {
    if (!active) return undefined;
    const handleClick = (event) => {
      if (!shouldGuardLinkClick(event)) return;
      if (!window.confirm(messageRef.current)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener("click", handleClick, true);
    return () => window.removeEventListener("click", handleClick, true);
  }, [active]);
};

export default useUnsavedLinkGuard;
